import { Alert, Center, Loader, Paper, Stack } from '@mantine/core';
import { getFormattedDate } from '@mantine/dates';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useState } from 'react';
import { frequencyAmountCell, SectionTotal } from 'src/cashflows/components/cashflowCells';
import CashflowSection from 'src/cashflows/components/CashflowSection';
import CashflowTable, { CashflowColumn } from 'src/cashflows/components/CashflowTable';
import { labelForIncomeType } from 'src/cashflows/incomeTypes';
import { Frequency } from 'src/cashflows/utils';
import { formatMoney, TenantCurrency } from 'src/common/currency';
import { useCurrency } from 'src/common/hooks/useCurrency';
import { CashflowModelsSection } from 'src/cashflows/modelling/CashflowModelsSection';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { NameFormat, formatName } from 'src/utils/nameFormatting';
import { FamilyMember } from './useFamilyMembers';
import {
  FamilyExpenditure,
  FamilyIncome,
  FamilyLifeEvent,
  useFamilyCashflows,
} from './useFamilyCashflows';

export function FamilyCashflowPanel({
  familyId,
  members,
}: {
  familyId: string;
  members: FamilyMember[];
}) {
  const currency = useCurrency();
  const { incomes, expenditures, lifeEvents, isLoading, hasErrors } = useFamilyCashflows(members);
  const { flagEnabledCashflowModelling, flagEnabledFamilyCashflowModelling } = useFlags();
  const showModels = flagEnabledCashflowModelling && flagEnabledFamilyCashflowModelling;
  const selectableMembers = members.map(member => ({
    clientId: member.clientId,
    name: formatName(member.client.firstName, member.client.lastName, NameFormat.NATURAL),
  }));
  const modelsSection = showModels ? (
    <CashflowModelsSection scope={{ kind: 'family', id: familyId }} members={selectableMembers} />
  ) : null;

  const [incomeFrequency, setIncomeFrequency] = useState<Frequency>('monthly');
  const [expenditureFrequency, setExpenditureFrequency] = useState<Frequency>('monthly');

  if (isLoading) {
    return (
      <Paper variant="content">
        <Center py="xxl">
          <Loader />
        </Center>
      </Paper>
    );
  }

  const incomeMonthlyPreTax = incomes.reduce((sum, item) => sum + item.amountPreTaxMonthly, 0);
  const incomeMonthlyPostTax = incomes.reduce((sum, item) => sum + item.amountPostTaxMonthly, 0);
  const incomeAnnualPreTax = incomes.reduce((sum, item) => sum + item.amountPreTaxAnnually, 0);
  const incomeAnnualPostTax = incomes.reduce((sum, item) => sum + item.amountPostTaxAnnually, 0);

  const incomeIsMonthly = incomeFrequency === 'monthly';
  const incomeTotalPreTax = incomeIsMonthly ? incomeMonthlyPreTax : incomeAnnualPreTax;
  const incomeTotalPostTax = incomeIsMonthly ? incomeMonthlyPostTax : incomeAnnualPostTax;
  const incomeTotalTooltip = incomeIsMonthly
    ? `Annually: +${formatMoney(incomeAnnualPreTax, currency)} (${formatMoney(incomeAnnualPostTax, currency)} after tax)`
    : `Monthly: +${formatMoney(incomeMonthlyPreTax, currency)} (${formatMoney(incomeMonthlyPostTax, currency)} after tax)`;

  const monthlyExpenditure = expenditures.reduce((sum, item) => sum + item.amountMonthly, 0);
  const annualExpenditure = expenditures.reduce((sum, item) => sum + item.amountAnnually, 0);
  const expenditureTotal =
    expenditureFrequency === 'monthly' ? monthlyExpenditure : annualExpenditure;
  const expenditureTotalTooltip =
    expenditureFrequency === 'monthly'
      ? `Annually: -${formatMoney(annualExpenditure, currency)}`
      : `Monthly: -${formatMoney(monthlyExpenditure, currency)}`;

  return (
    <Stack gap="md">
      {modelsSection}
      {hasErrors && (
        <Alert
          variant="light"
          color="yellow"
          icon={<IconAlertTriangle size={18} />}
          title="Some data could not be loaded"
        >
          Cashflow data for one or more family members failed to load, so the totals below may be
          incomplete.
        </Alert>
      )}

      <CashflowSection
        value="incomes"
        title="Income"
        count={incomes.length}
        frequency={incomeFrequency}
        onFrequencyChange={setIncomeFrequency}
        showFrequencyToggle
        totalNode={
          <SectionTotal tooltipLabel={incomeTotalTooltip}>
            +{formatMoney(incomeTotalPreTax, currency)} ({formatMoney(incomeTotalPostTax, currency)}{' '}
            after tax)
          </SectionTotal>
        }
      >
        <CashflowTable<FamilyIncome>
          items={incomes}
          columns={incomeColumns(incomeFrequency, currency)}
        />
      </CashflowSection>

      <CashflowSection
        value="expenditures"
        title="Expenditure"
        count={expenditures.length}
        frequency={expenditureFrequency}
        onFrequencyChange={setExpenditureFrequency}
        showFrequencyToggle
        totalNode={
          <SectionTotal tooltipLabel={expenditureTotalTooltip}>
            -{formatMoney(expenditureTotal, currency)}
          </SectionTotal>
        }
      >
        <CashflowTable<FamilyExpenditure>
          items={expenditures}
          columns={expenditureColumns(expenditureFrequency, currency)}
        />
      </CashflowSection>

      <CashflowSection
        value="lifeEvents"
        title="Future life events"
        count={lifeEvents.length}
        totalNode={
          <SectionTotal>
            -
            {formatMoney(
              lifeEvents.reduce((sum, item) => sum + (item.cost || 0), 0),
              currency,
            )}
          </SectionTotal>
        }
      >
        <CashflowTable<FamilyLifeEvent> items={lifeEvents} columns={lifeEventColumns(currency)} />
      </CashflowSection>
    </Stack>
  );
}

function incomeColumns(
  frequency: Frequency,
  currency: TenantCurrency,
): CashflowColumn<FamilyIncome>[] {
  const preTaxKey = frequency === 'monthly' ? 'amountPreTaxMonthly' : 'amountPreTaxAnnually';
  const postTaxKey = frequency === 'monthly' ? 'amountPostTaxMonthly' : 'amountPostTaxAnnually';

  return [
    { key: 'memberName', header: 'Member' },
    {
      key: 'incomeType',
      header: 'Type',
      renderValue: value => labelForIncomeType(String(value)),
    },
    { key: 'description', header: 'Source' },
    { key: 'frequency', header: 'Frequency' },
    {
      key: preTaxKey,
      header: 'Before tax',
      renderValue: (_value, item) =>
        frequencyAmountCell(
          frequency,
          item.amountPreTaxMonthly,
          item.amountPreTaxAnnually,
          currency,
          { positive: true },
        ),
    },
    {
      key: postTaxKey,
      header: 'After tax',
      renderValue: (_value, item) =>
        frequencyAmountCell(
          frequency,
          item.amountPostTaxMonthly,
          item.amountPostTaxAnnually,
          currency,
          { positive: true },
        ),
    },
  ];
}

function expenditureColumns(
  frequency: Frequency,
  currency: TenantCurrency,
): CashflowColumn<FamilyExpenditure>[] {
  const amountKey = frequency === 'monthly' ? 'amountMonthly' : 'amountAnnually';

  return [
    { key: 'memberName', header: 'Member' },
    { key: 'category', header: 'Category' },
    { key: 'frequency', header: 'Frequency' },
    {
      key: amountKey,
      header: `Spend (${frequency})`,
      renderValue: (_value, item) =>
        frequencyAmountCell(frequency, item.amountMonthly, item.amountAnnually, currency),
    },
  ];
}

const lifeEventColumns = (currency: TenantCurrency): CashflowColumn<FamilyLifeEvent>[] => [
  { key: 'memberName', header: 'Member' },
  { key: 'description', header: 'Description' },
  {
    key: 'eventDate',
    header: 'Date',
    renderValue: value =>
      getFormattedDate({
        type: 'default',
        date: new Date(String(value)),
        locale: 'en',
        format: 'D MMMM YYYY',
        labelSeparator: '',
      }),
  },
  { key: 'details', header: 'Details' },
  {
    key: 'cost',
    header: 'Amount',
    renderValue: value => formatMoney(Number(value), currency),
  },
];
